import {ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {filter, map, switchMap, take, tap} from 'rxjs/operators';
import {
    AddPinOverridesDialogComponent,
} from '@emm/shared/dialogs/add-pin-overrides-dialog/add-pin-overrides-dialog.component';
import { PinOverrideGroup } from '@emm/core/models/pin-override-group.model';
import * as RA from 'ramda-adjunct';
import { Store } from '@ngrx/store';
import * as R from 'ramda';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PinOverridesFormComponent} from '@emm/management/components/pin-overrides-form/pin-overrides-form.component';
import {ContextMenuAction, GenericItem} from '@emm/core/models/context-menu-action.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {ManagementFacadeService} from '@emm/management/services/management-facade.service';
import {ManagementStoreService} from '@emm/management/services/management-store.service';
import {LocationSetTypesActions} from '@emm/management/store/location-set-types/location-set-types.actions';
import {LocationSetTypesSelectors} from '@emm/management/store/location-set-types/location-set-types.selectors';
import {LocationSetsSelectors} from '@emm/management/store/location-sets/location-sets.selectors';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {SearchFilter} from '@emm/core/models/search-filter';
import {UserService} from '@emm/core/services/user.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {Screen} from '@emm/core/models/screen.model';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {API_ERRORS} from "@emm/core/util/api-constants";


@Component({
    selector: 'emm-pin-overrides',
    styleUrls: ['./pin-overrides.component.scss'],
    templateUrl: './pin-overrides.component.html',
    standalone: false
})
export class PinOverridesComponent implements OnInit {

    // Note: PIN Overrides pages no longer utilizes the NgRx flow in favor of a more direct approach similar to Consumer Manager

    @ViewChild(PinOverridesFormComponent) pinOverridesForm: PinOverridesFormComponent;

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnly = this.userService.hasLoginRole();
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    isAuthor = this.userService.hasAuthorRole();
    isMMSAuthor = this.userService.isMmsAuthorOnly();
    isRestoreVisible = false;
    areEditButtonsVisible = false;
    isEditLocationDisabled = true;
    forceOverride = false;
    invalidRmi = false;

    childLocationSets$;
    locationSetTypes$;
    selectedPinOverrideGroup$ = new BehaviorSubject<PinOverrideGroup>(null);
    isLocationSetTypeParent$ = this.managementFacade.isLocationSetTypeParent$;
    parentLabel$ = null;
    parentLocationSets$ = this.managementFacade.parentLocationSets$;
    selectedLocationSetType$ = this.managementStore.queryLocationSetTypes.selected$;
    selectedLocationSet$ = this.managementFacade.selectedLocationSet$;

    centerLoading$ = of(false);
    rightLoading$ = of(false);
    nextScreen: Screen;
    publishPinsToggle$: Observable<boolean>;
    isSelectedGroupActive = new BehaviorSubject<boolean>(false);

    pinOverrideGroupsContextMenuActions = [
        {
            enabled: R.T,
            click: (group) => this.onDeleteGroup(group),
            visible: this.isSuperAdmin || this.isAdmin ? R.T : R.F,
            icon: R.always('delete'),
            text: R.always('Delete')
        }
    ] as ContextMenuAction<PinOverrideGroup>[];

    pinOverridesCache = [];
    pinOverrides$ = new BehaviorSubject<PinOverrideGroup[]>(null);
    pinOverridesAsGenericItems$ = this.pinOverrides$.pipe(
        filter(RA.isNotNil),
        map((pinOverrides) =>
            pinOverrides.map(
                (pinOverrideGroup) =>
                this.toGeneric(pinOverrideGroup)
            )
        )
    );
    selectedGeneric: GenericItem<PinOverrideGroup>;

    fields$ = of([
        {
            key: 'pinOverrideGroupName',
            type: 'input',
            templateOptions: {
                label: 'Name',
                placeholder: 'Enter PIN Override Group Name',
                required: false
            }
        }
    ]);

    constructor(
        private authFacade: AuthFacadeService,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private managementApiService: ManagementApiService,
        private notificationService: NotificationService,
        public managementFacade: ManagementFacadeService,
        private store: Store,
        private managementStore: ManagementStoreService,
        private changeDetector: ChangeDetectorRef,
        private userService: UserService,
        private launchDarklyService: LaunchDarklyService,
    ) {}

    checkIfGroupHasChanges(pinOverrideGroup: PinOverrideGroup): Observable<boolean> {
        return this.isReadOnly$.pipe(
            take(1),
            switchMap((isReadOnly) => {
                if (this.hasFormChanges() && !isReadOnly && RA.isNotNil(pinOverrideGroup)) {
                    const message = `You have unsaved changes. Are you sure you want to leave this page?`;
                    return this.dialogService
                        .openConfirmDialog(message, pinOverrideGroup.name, null)
                        .afterClosed()
                        .pipe(
                            switchMap(res => {
                                return of(res);
                            })
                        );
                }
                return of(true);
            }),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    ngOnInit(): void {
        this.pinOverrides$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        this.getPinOverrides();
        this.store.dispatch(LocationSetTypesActions.load());
        this.locationSetTypes$ = this.store.select(LocationSetTypesSelectors.getValidAll);
        this.childLocationSets$ = this.store.select(LocationSetsSelectors.getFilteredLocationSets);
        this.publishPinsToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.PIN_OVERRIDES_PUBLISHING);
    }

    setRestoreVisible(isRestoreVisible: boolean): void {
        this.isRestoreVisible = isRestoreVisible;
    }

    setEditButtonsVisible(areEditButtonsVisible: boolean): void {
        this.areEditButtonsVisible = areEditButtonsVisible;
    }

    disableEditLocation(isEditLocationDisabled: boolean): void {
        this.isEditLocationDisabled = isEditLocationDisabled;
    }

    getPinOverrides(): void {
        this.managementApiService.getPinOverridesGroups()
            .pipe(take(1))
            .subscribe(result => {
            this.pinOverridesCache = result;
            this.pinOverrides$?.next(result);
        });
    }

    locationSetTypeSelected(selected: LocationSetType): void {
        this.managementFacade.selectLocationSetType(selected.tag);
    }

    locationSetSelected(selected: LocationSet): void {
        this.managementFacade.selectLocationSet(selected.tag);
    }

    resetSelectedLocationSet(): void {
        this.managementFacade.selectLocationSet(null);
    }

    filterLocationSets(locationSetFilter: string): void {
        this.managementFacade.applyLocationSetFilter(locationSetFilter);
    }

    parentLocationSetSelected(selected: LocationSet): void {
        this.managementFacade.selectParentLocationSet(selected.tag);
    }

    openPinOverridesDialog(): void {
        const dialogRef = this.dialog.open(AddPinOverridesDialogComponent, {
            width: '800px',
            data: {
                selected: this.selectedPinOverrideGroup$.getValue(),
                childLocationSets$: this.childLocationSets$,
                isLocationSetTypeParent$: this.isLocationSetTypeParent$,
                locationSetTypes$: this.locationSetTypes$,
                parentLabel$: this.parentLabel$,
                parentLocationSets$: this.parentLocationSets$,
                selectedLocationSetType$: this.managementStore.queryLocationSetTypes.selected$,
                selectedLocationSet$: this.selectedLocationSet$,
                managementFacade: this.managementFacade,
                dialog: this.dialog,
            }
        });
        dialogRef
            .afterClosed()
            .pipe(
                filter((newPinOverrideGroup) => newPinOverrideGroup),
                switchMap((newPinOverrideGroup) => this.managementApiService.savePinOverridesGroup(newPinOverrideGroup)),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe({
            next: () => {
                this.centerLoading$ = of(false);
                this.notificationService.success('Success creating PIN Override Group');
                this.getPinOverrides(); // reload list
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }

    toGeneric(pinOverride: PinOverrideGroup): GenericItem<PinOverrideGroup> {
        return {
            name: pinOverride?.name,
            id: pinOverride?.id,
            disabled: false,
            source: pinOverride
        };
    }
    hasFormChanges(): boolean {
        return this.pinOverridesForm?.form?.dirty
    }

    onSelectPinOverrideGroup(selected: GenericItem<PinOverrideGroup>): void {
        const prevGroup = this.selectedPinOverrideGroup$.getValue();
        if (this.hasFormChanges()) {
            const confirmDialog =
                this.dialogService.openConfirmDialog('You have unsaved changes. Are you sure you want to leave this page?',
                    prevGroup.name, null);
            confirmDialog.afterClosed()
                .pipe(take(1))
                .subscribe((result) => {
                    if (result) {
                        this.selectedPinOverrideGroup$.next(selected.source);
                        this.selectedGeneric = this.toGeneric(this.selectedPinOverrideGroup$.getValue());
                        this.isPinOverrideGroupActive(this.selectedPinOverrideGroup$.value);
                    }
                });
        } else {
            this.selectedPinOverrideGroup$.next(selected.source);
            this.selectedGeneric = this.toGeneric(this.selectedPinOverrideGroup$.getValue());
            this.isPinOverrideGroupActive(this.selectedPinOverrideGroup$.value);
        }
    }

    isPinOverrideGroupActive(pinOverrideGroup: PinOverrideGroup): void {
        const today = new Date();
        const effectiveDate = new Date(pinOverrideGroup.effectiveDate);
        const endDate = pinOverrideGroup.endDate === null ? null : new Date(pinOverrideGroup.endDate);
        if ((effectiveDate <= today && endDate === null) || (effectiveDate <= today && endDate > today)) {
            this.isSelectedGroupActive.next(true);
        } else {
            this.isSelectedGroupActive.next(false);
        }
    }

    onFilter(searchFilter: SearchFilter): void {
        this.filterNodes(searchFilter.value as string, searchFilter.key);
    }

    filterNodes(value: string, selected: string): void {
        this.pinOverrides$.next(this.pinOverridesCache);
        this.pinOverrides$.pipe(
            take(1),
            map(list => {
                const filteredNodes = list.filter(nd => nd['name'].toLowerCase().includes(value.toLowerCase()));
                this.pinOverrides$.next(filteredNodes);
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }

    onDeleteGroup(group: PinOverrideGroup): void {
        const id = group.id;
        if (id !== null){
            this.managementApiService.deletePinOverridesGroupById(id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response) => {
                        this.getPinOverrides();
                        this.selectedPinOverrideGroup$.next(null);
                        this.notificationService.success('Delete succeeded.');
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                });
        } else{
            this.notificationService.error('Validation error (no ID for row). Could not perform delete.');
        }
    }

    handleForceOverride() {
        console.log(this.forceOverride)
        this.forceOverride = !this.forceOverride;
        console.log(this.forceOverride)
    };

    publishAllPins() {
        console.log(this.forceOverride)
        if (this.forceOverride) {
            const location = this.selectedPinOverrideGroup$.value.location;
            this.pinOverridesForm.publishAllPins(this.forceOverride, location).pipe(take(1)).subscribe({
                    next: () => {
                        this.notificationService.success('Pins published successfully');
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                }
            );
        } else {
            this.pinOverridesForm.publishAllPins().pipe(take(1)).subscribe({
                    next: () => {
                        this.notificationService.success('Pins published successfully');
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                }
            );
        }
    }

    handleRmiValidation(event) {
        this.invalidRmi = event;
        console.log(this.invalidRmi, event);
    }

}
